import * as React from 'react';
import styled from 'styled-components';
import { useState } from 'react';
import { useLocation } from 'react-router';
import { parse } from 'query-string';
import {
	CountryCode,
	OldCountryCode,
	countryCodesToMobileNumberLengths,
	countryCodesToCountryCodeNums,
	getPhoneNumberParts,
	pixelsToNumber,
	validateMobile
} from '../../lib/utils';
import FieldDecorator from './FieldDecorator';
import Option from '../../models/Option';
import {
	blueyGrey,
	dataCaptureFieldHeight,
	fieldBackgroundWhite,
	fieldBorder,
	metallicBlue,
	mobilePhoneSelectZIndex,
	oldFieldHeight
} from '../../appComponents/cssConstants';
import { countryIsUk } from '../../config.local';
import { getCountryNameForMobileCode } from '../../core/countryList';

// Note that this component has been duplicated to MobileNumber.tsx and needs to be kept the same.
const MobilePhoneField = (props: MobilePhoneFieldProps) => {
	const location = useLocation();
	const queryParams = parse(location.search);
	const [errorMessage, setErrorMessage] = useState('');
	const displayContents = queryParams.displayContents as string;
	const phoneNumberParam: PhoneNumber = {
		countryCode: countryIsUk ? OldCountryCode.ukCode : OldCountryCode.auCode,
		nationalNumber: props.internationalNumber
	};
	const initialMobileNumber =
		displayContents === 'false'
			? phoneNumberParam
			: getPhoneNumberParts(props.internationalNumber, props.countryCode, props.customerCountryISOCode);
	const [countryCode, setCountryCode] = useState(initialMobileNumber.countryCode);
	const [nationalNumber, setNationalNumber] = useState(initialMobileNumber.nationalNumber);

	React.useEffect(() => {
		const mobileNumber = initialMobileNumber.nationalNumber;
		if (mobileNumber) {
			const newNationalNumber = mobileNumber[0] === '0' ? mobileNumber.slice(1) : mobileNumber;
			changePhoneNumber(countryCode, newNationalNumber);
			setErrorMessage(validateMobile(newNationalNumber, countryCode));
			setNationalNumber(
				mobileNumber.includes(countryCodesToCountryCodeNums[countryCode])
					? mobileNumber.replace(countryCodesToCountryCodeNums[countryCode], '')
					: mobileNumber ?? ''
			);
		}
	}, []);

	const handleCountryCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const selectedCountry = e.target.options[e.target.selectedIndex].value;
		setCountryCode(selectedCountry);
		changePhoneNumber(selectedCountry, nationalNumber);
	};

	const handleNationalNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		const digitsOnly = value.replace(/\D/g, '');
		setNationalNumber(digitsOnly);
		setErrorMessage(validateMobile(digitsOnly, countryCode));
		changePhoneNumber(countryCode, digitsOnly);
	};

	const handleNationalNumberBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const { value } = e.target;
		const newNationalNumber = value[0] === '0' ? value.slice(1) : value;
		changePhoneNumber(countryCode, newNationalNumber);
	};

	const changePhoneNumber = (newCountryCode: string, newNationalNumber: string) => {
		let newErrorMessage = '';
		if (newNationalNumber.length > 3) {
			newErrorMessage = validateMobile(newNationalNumber, newCountryCode);
		}
		setErrorMessage(newErrorMessage);

		const phoneNumber = constructMobileNumber(newCountryCode, newNationalNumber, props.required);
		props.onValueChange(props.name, phoneNumber, newErrorMessage);
		if (props.extendName !== null) {
			props.onValueChange(props.extendName, newCountryCode, newErrorMessage);
		}
	};

	return (
		<FieldDecorator label={props.label ?? 'Mobile'} htmlFor={props.name} errorMessage={errorMessage}>
			<Wrapper dataCaptureFieldStyling={props.dataCaptureFieldStyling}>
				<StyledSelect
					name={props.extendName}
					onChange={handleCountryCodeChange}
					defaultValue={countryCode}
					disabled={props.disabled}
					dataCaptureFieldStyling={props.dataCaptureFieldStyling}
					title={getCountryNameForMobileCode(countryCode)}
				>
					{props.supportAllInternational
						? getCountryCodeOptions().map((option) => (
								// eslint-disable-next-line react/jsx-indent
								<option key={option.value} value={option.value} title={getCountryNameForMobileCode(option?.value)}>
									{option.text}
								</option>
						  ))
						: getOldCountryCodeOptions().map((option) => (
								// eslint-disable-next-line react/jsx-indent
								<option key={option.value} value={option.value} title={getCountryNameForMobileCode(option?.value)}>
									{option.text}
								</option>
						  ))}
				</StyledSelect>
				<StyledInput
					name={props.name}
					value={nationalNumber}
					onChange={handleNationalNumberChange}
					onBlur={handleNationalNumberBlur}
					maxLength={getMobileTextFieldMaxLength(countryCode)}
					pattern="[0-9]{8,13}"
					inputMode="numeric"
					placeholder="Enter mobile number"
					title="Please input valid mobile number"
					disabled={props.disabled}
					required={false}
					dataCaptureFieldStyling={props.dataCaptureFieldStyling}
				/>
			</Wrapper>
		</FieldDecorator>
	);
};

export default MobilePhoneField;

interface MobilePhoneFieldProps {
	internationalNumber: string;
	countryCode?: string;
	onValueChange: (name: string, value: string, errorMessage: string) => void;
	name: string;
	extendName?: string;
	disabled?: boolean;
	required: boolean;
	dataCaptureFieldStyling?: boolean;
	supportAllInternational?: boolean;
	label?: string;
	customerCountryISOCode?: string;
}

interface PhoneNumber {
	countryCode: string;
	nationalNumber: string;
}

MobilePhoneField.defaultProps = {
	disabled: false,
	dataCaptureFieldStyling: false,
	supportAllInternational: false,
	extendName: null,
	label: null,
	countryCode: null,
	customerCountryISOCode: null
};

export const getMobileTextFieldMaxLength = (countryCode: string) => {
	if (countryCode in countryCodesToMobileNumberLengths) {
		return countryCodesToMobileNumberLengths[countryCode] + 1;
	}
	return 13;
};

export const constructMobileNumber = (countryCode: string, nationalNumber: string, required: boolean) => {
	if (required === false && nationalNumber === '') {
		return '';
	}
	return `${countryCodesToCountryCodeNums[countryCode]}${nationalNumber}`;
};

export const getCountryCodeOptions = (): Option<string>[] => {
	return Object.values(CountryCode).map((countryCode) => {
		return {
			text: `${countryCode} ${countryCodesToCountryCodeNums[countryCode]}`,
			value: countryCode
		};
	});
};

export const getOldCountryCodeOptions = (): Option<string>[] => {
	return Object.values(OldCountryCode).map((countryCode) => {
		return {
			text: `${countryCode} ${countryCodesToCountryCodeNums[countryCode]}`,
			value: countryCode
		};
	});
};

export const Wrapper = styled.div<{ dataCaptureFieldStyling: boolean }>`
	position: relative;
	display: flex;
	align-items: center;
	border: ${(props) => (props.dataCaptureFieldStyling ? fieldBorder : `1px solid ${blueyGrey}`)};
	border-radius: ${(props) => (props.dataCaptureFieldStyling ? '8px' : 'unset')};
`;

export const StyledSelect = styled.select<{ dataCaptureFieldStyling: boolean }>`
	left: 1px;
	font-size: 13px;
	font-family: 'sofia-pro', sans-serif;
	border: none;
	z-index: ${mobilePhoneSelectZIndex};
	color: ${metallicBlue};
	background-color: white;
	border-radius: ${(props) => (props.dataCaptureFieldStyling ? '8px' : 'unset')};
	height: ${(props) =>
		props.dataCaptureFieldStyling
			? `${pixelsToNumber(dataCaptureFieldHeight) - 2}px` // 2 is the top and bottom border for Input
			: `${pixelsToNumber(oldFieldHeight) - 2}px`};

	:disabled {
		cursor: not-allowed;
	}
`;

export const StyledInput = styled.input<{ dataCaptureFieldStyling: boolean }>`
	width: 100% !important;
	border: none;
	color: ${metallicBlue};
	border-radius: ${(props) => (props.dataCaptureFieldStyling ? '8px' : 'unset')};
	height: ${(props) => (props.dataCaptureFieldStyling ? dataCaptureFieldHeight : oldFieldHeight)};
	background-color: ${(props) => (props.dataCaptureFieldStyling ? fieldBackgroundWhite : `white`)};

	:disabled {
		cursor: not-allowed;
		border-color: rgb(168, 183, 199);
		background-color: rgb(248, 247, 243);
		opacity: 0.6;
	}
`;
